<template>
    <b-modal
        v-model="shown"
        centered
        :title="title"
        size="xl"
        lazy
    >
        <div>
            <!-- Type -->
            <b-form-group
                label="Type"
                :label-cols="labelCols"
            >
                <CostTypeSelect v-model="cost.type" />
            </b-form-group>
            <!--/ Type -->

            <!-- Status -->
            <b-form-group
                v-if="showStatus"
                label="Status"
                :label-cols="labelCols"
            >
                <CostableAddCostModalStatusSelect v-model="cost.status" />
            </b-form-group>
            <!--/ Status -->

            <!-- Secondary Status -->
            <b-form-group
                v-if="showStatus"
                label="Secondary status"
                :label-cols="labelCols"
            >
                <CostStatusSelect
                    v-model="cost.secondary_status"
                    type="secondary"
                />
            </b-form-group>
            <!--/ Secondary Status -->

            <!-- Supplier -->
            <b-form-group
                :label="$t('cost.supplier')"
                :label-cols="labelCols"
            >
                <AccountSearchSelect
                    v-model="cost.supplier"
                    :placeholder="'Search for a ' + $t('cost.supplier') + '...'"
                    :tag-placeholder="'Create ' + $t('cost.supplier') + '...'"
                />
            </b-form-group>
            <!--/ Supplier -->

            <hr>

            <!-- Description -->
            <b-form-group
                label="Description"
                :label-cols="labelCols"
            >
                <b-form-textarea
                    v-model="cost.description"
                    placeholder="Enter a description..."
                    rows="3"
                    max-rows="6"
                />
            </b-form-group>
            <!--/ Description -->

            <hr>

            <!-- Quantity -->
            <b-form-group
                :label="(cost.has_cost_quantity ? 'Price ' : '') + 'Quantity'"
                :label-cols="labelCols"
            >
                <QuantityAndUnitInputGroup v-model="cost.quantity" />
                <b-form-checkbox
                    v-model="cost.is_quantity_per_head"
                    class="mt-2"
                >
                    Per Head
                </b-form-checkbox>
                <div slot="description">
                    Tick this box if the quantity is linked to the number of guests.
                    Anything entered into the quantity field will be ignored and the number of guests will be used.
                </div>
            </b-form-group>
            <!--/ Quantity -->

            <hr>

            <b-form-group
                v-if="isCostQuantityEnabled"
                label="Cost Quantity"
                :label-cols="labelCols"
            >

                <!-- Add Cost Quantity Checkbox -->
                <b-form-checkbox
                    v-model="cost.has_cost_quantity"
                    class="mt-1 mb-2"
                >
                    Different quantity for unit cost
                </b-form-checkbox>
                <!--/ Add Duration Checkbox -->

                <b-input
                    v-if="cost.has_cost_quantity"
                    v-model="cost.cost_quantity"
                    class="mr-2"
                    type="text"
                    min="0"
                    step=".01"
                    placeholder="Enter a quantity..."
                />

            </b-form-group>

            <hr v-if="isCostQuantityEnabled">

            <!-- Duration -->
            <b-form-group
                v-if="isDurationEnabled"
                label="Duration"
                :label-cols="labelCols"
            >

                <!-- Add Duration Checkbox -->
                <b-form-checkbox v-model="cost.duration.enabled">
                    Add duration
                </b-form-checkbox>
                <!--/ Add Duration Checkbox -->

                <div v-if="cost.duration.enabled">

                    <!-- Enter Times Checkbox -->
                    <b-form-checkbox
                        v-model="cost.duration.time.enabled"
                        class="mb-2"
                    >
                        Enter start and finish times
                    </b-form-checkbox>
                    <!--/ Enter Times Checkbox -->

                    <!-- Time Input -->
                    <b-form-group v-if="cost.duration.time.enabled">
                        <div class="d-flex">
                            <b-input-group
                                prepend="Start"
                                class="mr-2"
                            >
                                <b-input
                                    v-model="cost.duration.time.start"
                                    type="time"
                                />
                            </b-input-group>
                            <b-input-group prepend="End">
                                <b-input
                                    v-model="cost.duration.time.end"
                                    type="time"
                                />
                            </b-input-group>
                        </div>
                    </b-form-group>
                    <!--/ Time Input -->

                    <!-- Number Input -->
                    <QuantityAndUnitInputGroup
                        v-else
                        v-model="cost.duration.quantity"
                    />
                    <!--/ Number Input -->

                </div>

            </b-form-group>
            <!--/ Duration -->

            <hr v-if="isDurationEnabled">

            <!-- Unit Cost -->
            <b-form-group
                label="Unit Cost"
                :label-cols="labelCols"
            >
                <NumericInputWithCurrencySelect
                    v-model="cost.unit_cost_local"
                    placeholder="Enter unit cost..."
                />
            </b-form-group>
            <!--/ Unit Cost -->

            <!-- Unit Price -->
            <b-form-group
                label="Unit Price"
                :label-cols="labelCols"
            >
                <NumericInputWithCurrencySelect
                    v-model="cost.unit_price_local"
                    placeholder="Enter unit price..."
                />
            </b-form-group>
            <!--/ Unit Price -->

            <!-- Unit Price Unit -->
            <b-form-group
                v-if="isPriceUnitEnabled"
                label="Unit Price Unit"
                :label-cols="labelCols"
            >
                <b-input
                    v-model="cost.unit_price.unit"
                    placeholder="e.g. pp, per person, etc"
                />
            </b-form-group>
            <!--/ Unit Price Unit -->

            <hr>

            <!-- Discount -->
            <b-form-group>
                <AbsoluteOrRelativeCurrencyToggleInputGroup
                    :value="cost.discount.value"
                    :fixed="cost.discount.fixed"
                    input-label="Discount"
                    input-placeholder="Enter discount..."
                    input-validation-required-message="Please enter a discount amount."
                    checkbox-label="Fixed"
                    checkbox-hint="Use a fixed discount value (e.g. £100) instead of a percentage of the unit cost/price."
                    @value="(value) => cost.discount.value = value"
                    @fixed="(fixed) => cost.discount.fixed = fixed"
                />
            </b-form-group>
            <!--/ Discount -->

            <hr>

            <b-form-group
                label="Management Fee"
                :label-cols="4"
            >
                <b-input-group>
                    <b-input-group-text slot="prepend">
                        %
                    </b-input-group-text>
                    <b-form-input
                        v-model="cost.management_fee"
                    />
                </b-input-group>
            </b-form-group>

            <hr>

            <!-- Commission -->
            <b-form-group>
                <AbsoluteOrRelativeCurrencyToggleInputGroup
                    :value="cost.commission.value"
                    :fixed="cost.commission.fixed"
                    input-label="Commission"
                    input-placeholder="Enter commission..."
                    input-validation-required-message="Please enter a commission amount."
                    checkbox-label="Fixed"
                    checkbox-hint="Use a fixed commission value (e.g. £100) instead of a percentage of the unit cost/price."
                    @value="(value) => cost.commission.value = value"
                    @fixed="(fixed) => cost.commission.fixed = fixed"
                />

            </b-form-group>
            <!--/ Commission -->

            <b-form-group
                label="Commission type"
                :label-cols="labelCols"
            >
                <b-form-radio-group
                    v-model="cost.commission.type"
                    class="mt-2"
                >
                    <b-form-radio value="receivable">
                        Receivable
                    </b-form-radio>
                    <b-form-radio value="payable">
                        Payable
                    </b-form-radio>
                </b-form-radio-group>
            </b-form-group>

            <hr>

            <!-- Billable & Vatable-->
            <b-form-group :label-cols="labelCols">
                <b-form-checkbox v-model="cost.billable">
                    Billable
                </b-form-checkbox>
                <b-form-checkbox v-model="cost.vatable">
                    Taxable
                </b-form-checkbox>
            </b-form-group>
            <!--/ Billable & Vatable-->

            <!-- Type -->
            <b-form-group
                v-if="cost.vatable"
                label="Sales tax rate"
                :label-cols="labelCols"
            >
                <TaxRateSelect v-model="cost.taxRate" />
            </b-form-group>
            <hr>

            <!-- Notes -->
            <b-form-group
                label="Notes"
                :label-cols="labelCols"
            >
                <froala
                    v-model="cost.notes"
                    :tag="'textarea'"
                    :config="froalaConfig"
                    placeholder="Enter notes..."
                    rows="3"
                    max-rows="6"
                />
            </b-form-group>
            <!--/ Notes -->

        </div>

        <div slot="modal-footer">
            <b-btn
                variant="default"
                class="mr-2"
                @click="reset()"
            >
                Close
            </b-btn>
            <Button
                variant="success"
                :loading="submitLoading"
                @click="handleSubmit()"
            >
                Save
            </Button>
        </div>
    </b-modal>
</template>

<script>
import vueComponentReset from '@ianwalter/vue-component-reset';
import CostTypeSelect from '@/components/CostTypeSelect';
import TaxRateSelect from '@/components/TaxRateSelect';
import QuantityAndUnitInputGroup from '@/components/QuantityAndUnitInputGroup';
import costTemplate from '@/models/cost';
import CostStatusSelect from '@/components/CostStatusSelect';
import CostableAddCostModalStatusSelect from './CostableAddCostModalStatusSelect';

const resetMixin = vueComponentReset(() => {
    const cost = JSON.parse(JSON.stringify(costTemplate));

    return {
        shown: false,
        submitLoading: false,
        title: '',
        cost,
    };
});

export default {

    components: {
        TaxRateSelect,
        CostTypeSelect,
        CostableAddCostModalStatusSelect,
        QuantityAndUnitInputGroup,
        CostStatusSelect,
    },

    mixins: [resetMixin],

    data() {
        const mediaURL = '/api/media';

        return {
            labelCols: 4,
            froalaConfig: {
                fileUploadURL: mediaURL,
                imageUploadURL: mediaURL,
            },
            showStatus: true,
        };
    },

    computed: {
        isDurationEnabled() {
            return this.$store.getters['costable/isDurationEnabled'];
        },
        isPriceUnitEnabled() {
            return this.$store.getters['costable/isPriceUnitEnabled'];
        },
        isCostQuantityEnabled() {
            return this.$store.getters['costable/isCostQuantityEnabled'];
        },
    },

    created() {
        this.$eventBus.$on(this.$events.openAddCostModal, (e) => {
            this.reset();
            this.title = 'Add Cost';
            this.cost.groupId = e.groupId || null;
            this.cost.commission.type = this.$store.getters['costable/settings'].default_payable_commission_type ? 'payable' : 'receivable';
            this.showStatus = !this.$store.getters['costable/settings'].hide_cost_statuses;
            this.shown = true;
        });
        this.$eventBus.$on(this.$events.openEditCostModal, (e) => {
            this.reset();
            this.title = 'Edit Cost';
            const eCost = JSON.parse(JSON.stringify(e.cost));
            this.showStatus = !this.$store.getters['costable/settings'].hide_cost_statuses;
            this.cost = {
                ...this.cost,
                ...eCost,
                // what we do here is hacky and should be refactored
                // but we are overriding the quantity.value with original_quantity
                // and this is because the QuantityAndUnitInputGroup component
                // expects an object with unit and value and original quantity only has value.
                // Refactor might be to let QuantityAndUnitInputGroup take a unit and value prop separately,
                // so we can pass it unit:quantity.unit and value:original_quantity.
                quantity: {
                    ...eCost.quantity,
                    value: eCost.original_quantity,
                },
            };
            this.shown = true;
        });
    },

    methods: {
        async handleSubmit() {
            this.submitLoading = true;
            if (this.cost.id) {
                await this.$store.dispatch('costable/updateCost', this.cost);
            } else {
                await this.$store.dispatch('costable/storeCost', this.cost);
            }
            this.submitLoading = false;
            this.shown = false;
        },
    },

};
</script>
